/**
 * The tabs of the customer database that are Connect's own, not a client's:
 * the register of customers, the cases waiting for a person, and the history.
 *
 * A customer is one person across every client (`docs/37`). The register finds
 * them by name, or exactly by a number or an email however it is typed. A case
 * is something the engine would not decide alone — two customers that may be
 * one, or a number now under another name (a recycled number, common in
 * India) — shown with both sides and the evidence, settled with one click.
 * Nothing here changes any client's contact.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, BadgeCheck, CheckCircle2, GitMerge, RefreshCw, Split, Users } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  Checkbox,
  Confirm,
  EmptyState,
  Pager,
  QueryPending,
  RecordSelect,
  Select,
  ServerTable,
  Textarea,
  cn,
  createServerFetcher,
  formatDate,
  formatDateTime,
  formatNumber,
  humanize,
  toast,
  type ServerColumn,
} from '@connect/web-shared';

import {
  BASE,
  BASIS,
  CONFIDENCE,
  LINK_STATUS,
  identityApi,
  identityKeys,
  showIdentifier,
  showMobile,
  type CustomerRow,
  type Decision,
  type IdentityEvent,
  type Party,
  type Review,
} from './identity-api';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : undefined);

// ── the register ──────────────────────────────────────────
type Shown = 'active' | 'merged' | 'all';

export function ConnectCustomers() {
  const navigate = useNavigate();
  const [shown, setShown] = useState<Shown>('active');
  const [review, setReview] = useState(false);
  const [client, setClient] = useState<string | null>(null);
  const extra = useMemo(
    () => ({ status: shown, review: review ? 'true' : undefined, client: client ?? undefined }),
    [shown, review, client],
  );
  const fetchRows = useMemo(() => createServerFetcher<CustomerRow>(BASE, extra), [extra]);
  const columns = useMemo<ServerColumn<CustomerRow>[]>(
    () => [
      {
        field: 'name',
        header: 'Customer',
        minWidth: 200,
        render: (row) => (
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-heading">{row.name ?? <span className="text-faint">No name yet</span>}</span>
            {row.status === 'merged' ? <Badge>Merged</Badge> : null}
          </span>
        ),
      },
      {
        field: 'name_confidence',
        header: 'Name certainty',
        filter: 'select',
        options: Object.entries(CONFIDENCE).map(([value, v]) => ({ value, label: v.label })),
        width: 130,
        render: (row) => <Badge tone={CONFIDENCE[row.name_confidence].tone}>{CONFIDENCE[row.name_confidence].label}</Badge>,
      },
      {
        field: 'mobiles',
        header: 'Mobiles',
        sortable: false,
        minWidth: 170,
        render: (row) => <Values values={row.mobiles.map(showMobile)} mono />,
      },
      {
        field: 'emails',
        header: 'Emails',
        sortable: false,
        minWidth: 200,
        render: (row) => <Values values={row.emails} />,
      },
      {
        field: 'client_count',
        header: 'Clients',
        width: 100,
        numeric: true,
        render: (row) => (row.client_count > 1 ? <Badge tone="accent">{row.client_count}</Badge> : row.client_count),
      },
      {
        field: 'open_reviews',
        header: 'Cases',
        width: 90,
        numeric: true,
        render: (row) => (row.open_reviews ? <Badge tone="warning">{row.open_reviews}</Badge> : <span className="text-faint">—</span>),
      },
      {
        field: 'last_seen_at',
        header: 'Last seen',
        width: 130,
        format: (value) => formatDate(value as string),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-1 flex-col gap-4">
      <StatusPanel />
      <ServerTable<CustomerRow>
        columns={columns}
        fetchRows={fetchRows}
        rowKey={(row) => row.public_id}
        queryKey={[...identityKeys.register, extra]}
        stateKey="customers.connect"
        searchPlaceholder="Search by name, mobile or email"
        onView={(row) => navigate(`/customers/${row.public_id}`)}
        toolbarActions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-36">
              <Select
                aria-label="Show"
                value={shown}
                onChange={(event) => setShown(event.target.value as Shown)}
                options={[
                  { value: 'active', label: 'Customers' },
                  { value: 'merged', label: 'Merged away' },
                  { value: 'all', label: 'Both' },
                ]}
              />
            </div>
            <div className="w-56">
              <RecordSelect
                endpoint="/crm/clients"
                value={client}
                onChange={(value) => setClient(value)}
                toOption={(row) => ({ value: String(row.public_id), label: String(row.name) })}
                placeholder="Any client"
                clearable
              />
            </div>
            <Checkbox label="Has a case" checked={review} onChange={(event) => setReview(event.target.checked)} />
          </div>
        }
        emptyTitle="No customers"
        emptyDescription="Customers appear as clients add contacts — Connect reads each one and links it to the person it is."
      />
    </div>
  );
}

function Values({ values, mono }: { values: string[]; mono?: boolean }) {
  if (values.length === 0) return <span className="text-faint">—</span>;
  return (
    <span className={cn('flex flex-wrap items-center gap-x-2 gap-y-0.5', mono && 'font-mono text-xs')}>
      <span>{values[0]}</span>
      {values.length > 1 ? <span className="text-2xs text-faint">+{values.length - 1}</span> : null}
    </span>
  );
}

function StatusPanel() {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const status = useQuery({ queryKey: identityKeys.status, queryFn: identityApi.status, refetchInterval: 30_000 });
  const rebuild = useMutation({
    mutationFn: identityApi.rebuild,
    onSuccess: async (done) => {
      toast.success('Reading every contact again', `${formatNumber(done.queued)} contacts queued.`);
      await queryClient.invalidateQueries({ queryKey: identityKeys.all });
    },
    onError: (error) => toast.error('Nothing was queued', errorText(error)),
  });
  if (status.data === undefined) return <QueryPending query={status} label="Loading the totals" />;
  const s = status.data;
  return (
    <>
      {!s.enabled ? (
        <Alert tone="warning" title="Identification is switched off">
          Contacts are still queued and will be read when it is switched back on (CUSTOMER_IDENTITY_ENABLED).
        </Alert>
      ) : null}
      {/* One strip, not a row of cards: the register below is what the page is for. */}
      <Card className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5 text-xs text-muted">
        <Figure icon={<Users className="size-3.5" />} value={s.customers} label="customers" hint={`${formatNumber(s.links)} client records`} />
        <Figure
          icon={<GitMerge className="size-3.5" />}
          value={s.open_reviews}
          label={s.open_reviews === 1 ? 'case to decide' : 'cases to decide'}
          tone={s.open_reviews ? 'text-warning' : undefined}
          hint={s.merged ? `${formatNumber(s.merged)} merged` : undefined}
        />
        <Figure
          icon={<BadgeCheck className="size-3.5" />}
          value={s.identifiers}
          label="numbers and emails"
          hint={`${formatNumber(s.verified)} verified${s.last_verified_at ? `, checked ${formatDateTime(s.last_verified_at)}` : ''}`}
        />
        <Figure
          icon={<RefreshCw className="size-3.5" />}
          value={s.queued}
          label="waiting to be read"
          tone={s.failing ? 'text-danger' : undefined}
          hint={s.failing ? `${formatNumber(s.failing)} being retried` : undefined}
        />
        <Can permission="crm.customer_identity.manage">
          <button
            type="button"
            className="ml-auto text-accent underline-offset-2 hover:underline"
            onClick={() => setConfirming(true)}
          >
            Read every contact again
          </button>
        </Can>
      </Card>
      <Confirm
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Read every contact again?"
        message="Every live contact of every client is queued and read again with the current rules. Nothing a person decided is undone, and no client's contact is changed. It runs in the background."
        confirmLabel="Read all again"
        onConfirm={async () => {
          setConfirming(false);
          await rebuild.mutateAsync().catch(() => undefined);
        }}
      />
    </>
  );
}

function Figure({
  icon,
  value,
  label,
  hint,
  tone,
}: {
  icon: ReactNode;
  value: number;
  label: string;
  hint?: string;
  tone?: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-faint">{icon}</span>
      <span className={cn('text-sm font-semibold tabular-nums text-heading', tone)}>{formatNumber(value)}</span>
      <span>{label}</span>
      {hint ? <span className="text-faint">· {hint}</span> : null}
    </span>
  );
}

// ── the cases ─────────────────────────────────────────────
type Kind = 'all' | Review['kind'];

export function ReviewQueue() {
  const [kind, setKind] = useState<Kind>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const params = { page, page_size: pageSize, kind: kind === 'all' ? undefined : kind };
  const reviews = useQuery({
    queryKey: [...identityKeys.reviews, params],
    queryFn: () => identityApi.reviews(params),
    placeholderData: keepPreviousData,
  });
  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-64">
          <Select
            aria-label="Kind of case"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as Kind);
              setPage(1);
            }}
            options={[
              { value: 'all', label: 'Every case' },
              { value: 'same_person', label: 'Maybe one person' },
              { value: 'name_conflict', label: 'A name that does not fit' },
            ]}
          />
        </div>
        <p className="text-xs text-muted">
          Connect links records by number and email, and merges two customers only when two clients agree. Everything
          less certain waits here.
        </p>
      </div>
      {reviews.data === undefined ? (
        <QueryPending query={reviews} label="Loading the cases" />
      ) : reviews.data.rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<CheckCircle2 className="size-5" />}
            title="Nothing to decide"
            description="When Connect is unsure whether two records are one person, the case appears here."
          />
        </Card>
      ) : (
        <>
          {reviews.data.rows.map((review) => (
            <ReviewCard key={review.public_id} review={review} />
          ))}
          <Pager page={page} pageSize={pageSize} total={reviews.data.total} onPageChange={setPage} onPageSizeChange={setPageSize} />
        </>
      )}
    </div>
  );
}

function ReviewCard({ review }: { review: Review }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [moveNumber, setMoveNumber] = useState(false);
  const resolve = useMutation({
    mutationFn: (decision: Decision) =>
      identityApi.resolve(review.public_id, {
        decision,
        move_identifier: decision === 'different_person' ? moveNumber : false,
        note: note.trim() || undefined,
      }),
    onSuccess: async () => {
      toast.success('Decided');
      await queryClient.invalidateQueries({ queryKey: identityKeys.all });
    },
    onError: (error) => toast.error('Not decided', errorText(error)),
  });
  const evidence = review.evidence;
  const conflict = review.kind === 'name_conflict';
  const identifier = (evidence.identifier ?? null) as { kind: string; value: string; verified_at: string | null; held_since: string } | null;

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-heading">
            {conflict ? 'A name that does not fit the number' : 'Maybe one person'}
          </p>
          <p className="text-xs text-muted">
            {conflict
              ? `${review.link?.client.name ?? 'A client'} calls them “${String(evidence.contact_name ?? '')}”. The number may have been lent, shared, or given to somebody new.`
              : `${formatNumber(Number(evidence.clients ?? 0))} client record${Number(evidence.clients) === 1 ? '' : 's'} carry identifiers of both; ${formatNumber(Number(evidence.needed ?? 2))} are needed to merge without a person.${evidence.names === 'conflict' ? ' Their names disagree.' : ''}`}
          </p>
        </div>
        <span className="text-2xs text-faint">Opened {formatDateTime(review.created_at)}</span>
      </div>
      <div className="grid gap-px bg-line md:grid-cols-2">
        <PartyPanel party={review.customer} title={conflict ? 'The customer with this number' : 'One customer'} />
        {conflict && review.link ? (
          <div className="space-y-2 bg-surface p-4">
            <p className="text-2xs font-medium uppercase tracking-wide text-faint">The new record</p>
            <p className="text-sm font-medium text-heading">{review.link.observed_name ?? review.link.contact?.name ?? '—'}</p>
            <p className="text-xs text-muted">
              Kept by {review.link.client.name} since {formatDate(review.link.linked_at)}
            </p>
            {review.link.contact ? (
              <p className="font-mono text-xs">{[review.link.contact.mobile, review.link.contact.email].filter(Boolean).join(' · ')}</p>
            ) : null}
            {identifier ? (
              <p className="text-xs text-muted">
                The shared number is held by the customer since {formatDate(identifier.held_since)}
                {identifier.verified_at ? `; last verified ${formatDate(identifier.verified_at)}` : '; never verified'}.
              </p>
            ) : null}
          </div>
        ) : review.other ? (
          <PartyPanel party={review.other} title="The other customer" />
        ) : null}
      </div>
      <Can permission="crm.customer_identity.edit">
        <div className="space-y-3 border-t border-line p-4">
          <Textarea label="Note (optional)" rows={1} value={note} onChange={(event) => setNote(event.target.value)} />
          <div className="flex flex-wrap items-center gap-2">
            {conflict ? (
              <>
                <Button loading={resolve.isPending} onClick={() => resolve.mutate('same_person')}>
                  Same person
                </Button>
                <Button variant="secondary" loading={resolve.isPending} onClick={() => resolve.mutate('different_person')}>
                  <Split className="size-4" />
                  Different person
                </Button>
                <Checkbox
                  label="The number now belongs to the new person"
                  checked={moveNumber}
                  onChange={(event) => setMoveNumber(event.target.checked)}
                />
              </>
            ) : (
              <>
                <Button loading={resolve.isPending} onClick={() => resolve.mutate('merge')}>
                  <GitMerge className="size-4" />
                  Merge
                </Button>
                <Button variant="secondary" loading={resolve.isPending} onClick={() => resolve.mutate('keep_apart')}>
                  Keep apart
                </Button>
              </>
            )}
          </div>
        </div>
      </Can>
    </Card>
  );
}

function PartyPanel({ party, title }: { party: Party; title: string }) {
  const live = party.links.filter((link) => link.status !== 'ended');
  return (
    <div className="space-y-2 bg-surface p-4">
      <p className="text-2xs font-medium uppercase tracking-wide text-faint">{title}</p>
      <RouterLink to={`/customers/${party.public_id}`} className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline">
        {party.name ?? 'No name yet'}
        <ArrowRight className="size-3.5" />
      </RouterLink>
      <ul className="space-y-0.5 text-xs">
        {party.identifiers.map((identifier) => (
          <li key={identifier.public_id} className="flex flex-wrap items-center gap-1.5">
            <span className={cn(identifier.kind === 'mobile' && 'font-mono')}>{showIdentifier(identifier)}</span>
            {identifier.status === 'verified' ? <Badge tone="success">Verified</Badge> : null}
          </li>
        ))}
      </ul>
      <ul className="space-y-0.5 text-xs text-muted">
        {live.map((link) => (
          <li key={link.public_id}>
            {link.client.name}: “{link.observed_name ?? '—'}” <span className="text-faint">· {BASIS[link.basis]}</span>
            {link.status === 'provisional' ? <Badge tone={LINK_STATUS.provisional.tone} className="ml-1">{LINK_STATUS.provisional.label}</Badge> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── the history ───────────────────────────────────────────
const EVENT_LABELS: Record<string, string> = {
  created: 'Customer created',
  linked: 'Client record linked',
  relinked: 'Client record linked again',
  unlinked: 'Client record ended',
  identifier_added: 'Number or email added',
  identifier_moved: 'Number or email moved away',
  identifier_removed: 'Number or email no client keeps',
  address_removed: 'Address no client keeps',
  address_added: 'Address added',
  name_changed: 'Name changed',
  name_set: 'Name set by staff',
  merged: 'Merged in another customer',
  merged_into: 'Merged into another customer',
  unmerged: 'Merge undone',
  split: 'Records split off',
  split_from: 'Split off from another customer',
  review_opened: 'Case opened',
};

export function eventSummary(event: IdentityEvent): string {
  const d = event.detail;
  const parts: string[] = [];
  if (typeof d.value === 'string') parts.push(d.value);
  if (typeof d.name === 'string') parts.push(`“${d.name}”${typeof d.previous === 'string' ? ` (was “${d.previous}”)` : ''}`);
  if (typeof d.reason === 'string') parts.push(humanize(d.reason));
  if (typeof d.status === 'string' && event.kind === 'linked') parts.push(humanize(d.status));
  if (typeof d.basis === 'string') parts.push(BASIS[d.basis as keyof typeof BASIS] ?? humanize(d.basis));
  if (typeof d.review === 'string') parts.push(humanize(d.review));
  if (typeof d.city === 'string') parts.push(d.city);
  return parts.join(' · ');
}

export function eventLabel(kind: string): string {
  return EVENT_LABELS[kind] ?? humanize(kind);
}

export function IdentityHistory({ customer }: { customer?: string }) {
  const path = customer ? `${BASE}/${customer}/events` : `${BASE}/events`;
  const fetchRows = useMemo(() => createServerFetcher<IdentityEvent & { key: string }>(path), [path]);
  const columns = useMemo<ServerColumn<IdentityEvent & { key: string }>[]>(
    () => [
      { field: 'created_at', header: 'When', sortable: false, width: 170, format: (value) => formatDateTime(value as string) },
      ...(customer
        ? []
        : [
            {
              field: 'customer',
              header: 'Customer',
              sortable: false,
              minWidth: 180,
              render: (row: IdentityEvent) => (
                <RouterLink to={`/customers/${row.customer.public_id}`} className="text-accent hover:underline">
                  {row.customer.name ?? 'No name yet'}
                </RouterLink>
              ),
            } satisfies ServerColumn<IdentityEvent & { key: string }>,
          ]),
      { field: 'kind', header: 'What', sortable: false, minWidth: 200, render: (row) => eventLabel(row.kind) },
      { field: 'detail', header: 'Detail', sortable: false, minWidth: 220, render: (row) => eventSummary(row) || '—' },
      {
        field: 'actor',
        header: 'By',
        sortable: false,
        width: 140,
        render: (row) => row.actor ?? <span className="text-faint">Connect</span>,
      },
    ],
    [customer],
  );
  return (
    <ServerTable<IdentityEvent & { key: string }>
      columns={columns}
      fetchRows={fetchRows}
      rowKey={(row) => `${row.created_at}-${row.kind}-${row.customer.public_id}-${eventSummary(row)}`}
      queryKey={customer ? identityKeys.history(customer) : identityKeys.events}
      stateKey={customer ? 'customers.connect.history' : 'customers.connect.events'}
      search={false}
      emptyTitle="Nothing yet"
      emptyDescription="Every link, merge, split and decision is written here."
    />
  );
}
